// src/scenes/figagent/FIGAgentInvoiceBulkReview.jsx

import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Modal,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { styled, useTheme } from "@mui/material/styles";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import Header from "../../components/Header";
import * as pdfjsLib from "pdfjs-dist/build/pdf";
import { useAuth } from "../../context/AuthContext";

// PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc =
    `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// ENDPOINTS
const API_UPLOAD = "https://media.swancreate.com/upload";
const API_SCAN = "https://invoice.swancreate.com/scan_invoice/";
const API_VENDORS = "https://figazuresql.swancreate.com/api/swa/FINANCE_VendORS";
const API_SCHOOLS = "https://figazuresql.swancreate.com/api/swa/SchoolMst";
const API_HDR = "https://figazuresql.swancreate.com/api/swa/AZ_Invoice";
const API_ITEMS = "https://figazuresql.swancreate.com/api/swa/AZ_InvoiceItems";

// Fallback vendor name — make sure this exists verbatim in your FINANCE_VendORS table
const FALLBACK_VENDOR_NAME = "Unknown Vendor";

// STYLED COMPONENTS
const Thumbnail = styled("img")(({ theme }) => ({
    width: 80,
    height: 80,
    objectFit: "cover",
    cursor: "pointer",
    marginBottom: 8,
    borderRadius: 4,
    boxShadow: `0 0 5px ${theme.palette.secondary.main}`,
}));
const DropZone = styled(Box)(({ theme }) => ({
    border: `2px dashed ${theme.palette.secondary.main}`,
    borderRadius: 8,
    padding: 20,
    textAlign: "center",
    color: theme.palette.secondary.main,
    cursor: "pointer",
    marginBottom: 16,
}));

export default function FIGAgentInvoiceBulkReview() {
    const theme = useTheme();
    const { allocations } = useAuth();

    // State
    const [schools, setSchools] = useState([]);
    const [selectedSchool, setSelectedSchool] = useState("");
    const [files, setFiles] = useState([]);
    const [pdfPages, setPdfPages] = useState([]);
    const [invoiceData, setInvoiceData] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState("");
    const [reviewing, setReviewing] = useState(false);
    const [reviewProgress, setReviewProgress] = useState(0);

    // Load vendor + school master data
    useEffect(() => {
        axios.get(API_VENDORS)
            .then(r => setVendors(r.data))
            .catch(console.error);

        axios.get(API_SCHOOLS)
            .then(r => {
                // only schools manager can access
                const allowed = r.data.filter(s =>
                    allocations.some(a => a.SchoolId === s.SchoolId)
                );
                setSchools(allowed);
                if (allowed.length) setSelectedSchool(allowed[0].SchoolId);
            })
            .catch(console.error);
    }, [allocations]);

    // Normalize helper (strip punctuation & lowercase)
    const normalize = str =>
        (str || "")
            .toString()
            .toLowerCase()
            .replace(/[^a-z0-9]/g, "");

    // —————————————
    // FUZZY VENDOR MATCH
    // —————————————
    function matchVendor(rawOCR) {
        const key = normalize(rawOCR);
        let v = vendors.find(v =>
            normalize(v.VendorName).includes(key) ||
            key.includes(normalize(v.VendorName))
        );
        if (v) return v;
        v = vendors.find(vendor =>
            normalize(vendor.VendorName)
                .split("")
                .some((_, i, arr) =>
                    key.includes(arr.slice(0, i + 1).join(""))
                )
        );
        if (v) return v;
        const fallback = vendors.find(
            v => normalize(v.VendorName) === normalize(FALLBACK_VENDOR_NAME)
        );
        return fallback || {
            VendorID: null,
            VendorName: FALLBACK_VENDOR_NAME,
            CategoryID: null,
            CategoryName: "",
            SubCategoryID: null,
            SubCategoryName: "",
        };
    }

    // —————————————
    // FILE HANDLERS
    // —————————————
    const handleFiles = e => {
        Array.from(e.target.files).forEach(f => {
            if (f.type === "application/pdf" || /\.pdf$/i.test(f.name)) {
                splitPdf(f);
            } else {
                setFiles(prev => [...prev, f]);
            }
        });
    };
    const handleDrop = e => {
        e.preventDefault();
        handleFiles({ target: { files: e.dataTransfer.files } });
    };

    // —————————————
    // SPLIT PDF → IMAGES
    // —————————————
    const splitPdf = async file => {
        try {
            const buf = await file.arrayBuffer();
            const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const vp = page.getViewport({ scale: 2 });
                const canvas = document.createElement("canvas");
                canvas.width = vp.width;
                canvas.height = vp.height;
                await page.render({
                    canvasContext: canvas.getContext("2d"),
                    viewport: vp
                }).promise;
                const blob = await new Promise(r => canvas.toBlob(r, "image/jpeg"));
                setPdfPages(prev => [
                    ...prev,
                    { file: new File([blob], `${file.name}_p${i}.jpg`, { type: "image/jpeg" }), PageNum: i }
                ]);
            }
        } catch (e) {
            console.error("PDF split error:", e);
        }
    };

    // —————————————
    // UPLOAD → OCR → EXPORT
    // —————————————
    const handleUploadAll = async () => {
        if (!files.length && !pdfPages.length) {
            setError("No files to process.");
            return;
        }
        setError(""); setLoading(true); setProgress(0);

        const tasks = [
            ...files.map(f => ({ file: f, page: null })),
            ...pdfPages.map(p => ({ file: p.file, page: p }))
        ];

        for (let idx = 0; idx < tasks.length; idx++) {
            const { file, page } = tasks[idx];
            let imageURL = "";

            // 1) upload
            try {
                const ext = file.name.slice(file.name.lastIndexOf("."));
                const fn = `${uuidv4()}${ext}`;
                const form = new FormData();
                form.append("file", file, fn);
                form.append("folder", "invoices");
                await axios.post(API_UPLOAD, form);
                imageURL = `https://media.swancreate.com/images/invoices/${fn}`;
            } catch { /* ignore upload errors */ }

            // 2) OCR + normalize
            try {
                const sf = new FormData(); sf.append("file", file, file.name);
                const { data } = await axios.post(API_SCAN, sf);
                const invs = data.invoice_data || [];
                for (const raw of invs) {
                    const inv = { ...raw };
                    inv.apiid = inv.apiid || uuidv4();
                    inv.CustomerName = inv.CustomerName?.value || inv.CustomerName || "";
                    inv.InvoiceTotal = inv.InvoiceTotal?.value ?? inv.InvoiceTotal ?? 0;
                    inv.InvoiceNumber = inv.InvoiceId?.value || inv.InvoiceId || "";
                    inv.InvoiceDate = inv.InvoiceDate?.value || inv.InvoiceDate || null;
                    inv.imageURL = imageURL;
                    if (page) inv.PageNum = page.PageNum;

                    // vendor fuzzy
                    const v = matchVendor(inv.VendorName?.value || inv.VendorName);
                    inv.VendorID = v.VendorID;
                    inv.VendorName = v.VendorName;
                    inv.CategoryID = v.CategoryID;
                    inv.CategoryName = v.CategoryName;
                    inv.SubcategoryID = v.SubCategoryID;
                    inv.SubcategoryName = v.SubCategoryName;

                    // **assign chosen school**
                    const chosen = schools.find(s => s.SchoolId === selectedSchool);
                    inv.SchoolId = chosen?.SchoolId || null;
                    inv.SchoolName = chosen?.SchoolName || "Unknown School";

                    setInvoiceData(prev => [...prev, inv]);
                    await exportOne(inv);
                }
            } catch (e) {
                console.error("OCR failed for", file.name, e);
            }

            setProgress(idx + 1);
        }

        setLoading(false);
    };

    // —————————————
    // EXPORT HEADER + ITEMS
    // —————————————
    const exportOne = async inv => {
        const hdr = {
            apiid: inv.apiid,
            InvoiceNumber: inv.InvoiceNumber,
            VendorName: inv.VendorName,
            VendorID: inv.VendorID,
            CustomerName: inv.CustomerName,
            InvoiceDate: inv.InvoiceDate,
            InvoiceTotal: inv.InvoiceTotal,
            CategoryID: inv.CategoryID,
            CategoryName: inv.CategoryName,
            SubcategoryID: inv.SubcategoryID,
            SubcategoryName: inv.SubcategoryName,
            SchoolId: inv.SchoolId,
            SchoolName: inv.SchoolName,
            CreatedOn: new Date().toISOString(),
            PageNum: inv.PageNum || 1,
            imageURL: inv.imageURL,
        };
        try {
            await axios.post(API_HDR, hdr);
        } catch (e) {
            console.error("AZ_Invoice export failed:", e);
        }

        if (Array.isArray(inv.Items)) {
            for (const rawItem of inv.Items) {
                const it = {
                    apiid: uuidv4(),
                    InvoiceApiId: inv.apiid,
                    InvoiceNumber: inv.InvoiceNumber,
                    Description: rawItem.Description?.value || "",
                    Quantity: rawItem.Quantity?.value || 0,
                    Unit: rawItem.Unit?.value || "",
                    UnitPrice: rawItem.UnitPrice?.value || 0,
                    ProductCode: rawItem.ProductCode?.value || "",
                    ItemDate: rawItem.Date?.value || null,
                    Tax: rawItem.Tax?.value || "",
                    Amount: rawItem.Amount?.value ?? rawItem.Amount ?? 0,
                    CreatedOn: new Date().toISOString(),
                };
                try {
                    await axios.post(API_ITEMS, it);
                } catch (e) {
                    console.error("AZ_InvoiceItems export failed:", e);
                }
            }
        }
    };

    // —————————————
    // MARK HEADERS REVIEWED
    // —————————————
    const handleReviewComplete = async () => {
        if (!invoiceData.length) return;
        setReviewing(true);
        setReviewProgress(0);

        for (let i = 0; i < invoiceData.length; i++) {
            const inv = invoiceData[i];
            try {
                await axios.put(`${API_HDR}/${inv.apiid}`, { isReviewed: true });
            } catch (e) {
                console.error("Failed to mark invoice reviewed:", inv.apiid, e);
            }
            setReviewProgress(i + 1);
        }

        setReviewing(false);
        setFiles([]);
        setPdfPages([]);
        setInvoiceData([]);
    };

    // —————————————
    // GRID COLUMNS
    // —————————————
    const columns = [
        { field: "apiid", headerName: "API ID", flex: 1 },
        { field: "VendorName", headerName: "Vendor Name", flex: 1 },
        { field: "CustomerName", headerName: "Customer", flex: 1 },
        {
            field: "InvoiceDate",
            headerName: "Invoice Date",
            flex: 1,
            valueGetter: ({ row }) =>
                row.InvoiceDate
                    ? new Date(row.InvoiceDate).toLocaleDateString()
                    : "N/A",
        },
        { field: "InvoiceNumber", headerName: "Invoice #", flex: 1 },
        {
            field: "InvoiceTotal",
            headerName: "Invoice Total",
            flex: 1,
            valueGetter: ({ row }) =>
                new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                }).format(row.InvoiceTotal),
        },
        { field: "CategoryName", headerName: "Category", flex: 1 },
        { field: "SubcategoryName", headerName: "Subcategory", flex: 1 },
        { field: "SchoolId", headerName: "School ID", flex: 0.6 },
        { field: "SchoolName", headerName: "School Name", flex: 1 },
    ];

    return (
        <Box
            sx={{
                m: 2, p: 2,
                bgcolor: theme.palette.background.default,
                color: theme.palette.text.primary,
                borderRadius: 2,
                boxShadow: theme.shadows[3],
                minHeight: "100vh",
            }}
        >
            <Header
                title="FIG Agent Invoice Bulk Review"
                subtitle="Select site → upload PDFs/images → OCR → export to AZ_Invoice"
            />

            <FormControl sx={{ mb: 2, minWidth: 240 }}>
                <InputLabel>Select Site</InputLabel>
                <Select
                    value={selectedSchool}
                    label="Select Site"
                    onChange={e => setSelectedSchool(e.target.value)}
                >
                    {schools.map(s => (
                        <MenuItem key={s.SchoolId} value={s.SchoolId}>
                            {s.SchoolName}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {error && <Typography color="error" mb={2}>{error}</Typography>}

            <DropZone onDrop={handleDrop} onDragOver={e => e.preventDefault()}>
                Drag &amp; drop PDFs or images here, or click to browse
            </DropZone>

            <Box display="flex" gap={2} mb={2} alignItems="center">
                <input
                    type="file"
                    multiple
                    accept="image/*,application/pdf"
                    onChange={handleFiles}
                />
                <Button variant="contained" onClick={handleUploadAll}>
                    Process Invoices
                </Button>
                {invoiceData.length > 0 && (
                    <Button
                        variant="contained"
                        onClick={handleReviewComplete}
                        sx={{
                            backgroundColor: "yellow",
                            color: "black",
                            "&:hover": { backgroundColor: "gold" },
                        }}
                    >
                        Complete Review
                    </Button>
                )}
            </Box>

            <Box display="flex" gap={1} mb={2} sx={{ overflowX: "auto" }}>
                {files.map((f, i) => <Thumbnail key={i} src={URL.createObjectURL(f)} />)}
                {pdfPages.map((p, i) => <Thumbnail key={i} src={URL.createObjectURL(p.file)} />)}
            </Box>

            <Modal open={loading} hideBackdrop>
                <Box
                    sx={{
                        position: "absolute", top: "50%", left: "50%",
                        transform: "translate(-50%,-50%)",
                        bgcolor: theme.palette.background.paper,
                        p: 3, borderRadius: 2, textAlign: "center",
                    }}
                >
                    <CircularProgress size={48} sx={{ color: theme.palette.secondary.main }} />
                    <Typography mt={2}>
                        Processing {progress} of {files.length + pdfPages.length}
                    </Typography>
                </Box>
            </Modal>

            <Modal open={reviewing} hideBackdrop>
                <Box
                    sx={{
                        position: "absolute", top: "50%", left: "50%",
                        transform: "translate(-50%,-50%)",
                        bgcolor: theme.palette.background.paper,
                        p: 3, borderRadius: 2, textAlign: "center",
                    }}
                >
                    <CircularProgress size={48} sx={{ color: "yellow" }} />
                    <Typography mt={2}>
                        Reviewing {reviewProgress} of {invoiceData.length}
                    </Typography>
                </Box>
            </Modal>

            <Box mt={4}>
                <DataGrid
                    rows={invoiceData}
                    columns={columns}
                    getRowId={r => r.apiid}
                    autoHeight
                    pageSize={25}
                    rowsPerPageOptions={[25]}
                    components={{ Toolbar: GridToolbar }}
                    disableSelectionOnClick
                    sx={{
                        "& .MuiDataGrid-row:nth-of-type(odd)": {
                            backgroundColor: theme.palette.background.paper
                        },
                        "& .MuiDataGrid-row:nth-of-type(even)": {
                            backgroundColor: theme.palette.action.hover
                        }
                    }}
                />
            </Box>
        </Box>
    );
}
